import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@material-ui/core';
import DialogContentText from '@material-ui/core/DialogContentText';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { useState } from 'react';
import LoadingOverlay from 'react-loading-overlay';
import { useSelector } from 'react-redux';

import { CustomTable } from './file-list.styles';
import { parseFilename, properCase } from '../../../shared/helpers/string-helpers';
import { EnumFileStatus } from '../../redux/scholarship.models';
import { selectError, selectIsLoading } from '../../redux/scholarship.selectors';

interface FileRow {
  filePath?: string;
  docStatus?: EnumFileStatus;
}

interface FileListGridProps {
  files: FileRow[];
  scanning: boolean;
  handleFileDelete: (confirmation: string) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontWeight: 800
    },
    buttonSmall: {
      marginLeft: '10px',
      height: '25px',
      font: '15px;',
      backgroundColor: '#f1f1f1'
    }
  })
);

export const FileListGrid = ({ files, scanning, handleFileDelete }: FileListGridProps): JSX.Element => {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const isDeleting = useSelector(selectIsLoading);
  const errorDeleting = useSelector(selectError);
  const handleToggle = (status: boolean) => {
    setOpen(status);
  };

  const handleConfirmType = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setConfirmation(e.target.value.toLowerCase().trim());
  };

  const handleAppDialogDelete = () => {
    if (confirmation === 'permanently delete') {
      handleToggle(false);
      handleFileDelete(confirmation);
    }
  };

  return (
    <TableContainer component={Paper} style={{ marginTop: '20px' }}>
      {Boolean(errorDeleting) && <Alert severity="error">{String(errorDeleting)}</Alert>}
      <LoadingOverlay active={isDeleting} spinner text="Deleting file.">
        <CustomTable>
          <TableHead style={{ backgroundColor: '#f3f6fd' }}>
            <TableRow>
              <TableCell className={classes.title}>Filename</TableCell>
              <TableCell className={classes.title} align="right">
                Status
              </TableCell>
              <TableCell className={classes.title} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((row: FileRow, index: number) => (
              <TableRow key={index}>
                <TableCell component="th" scope="row" width={'auto'}>
                  {row.filePath ? parseFilename(row.filePath) : ''}
                </TableCell>
                <TableCell align="right">
                  {scanning && <CircularProgress size={12} />}
                  &nbsp;{row.docStatus === EnumFileStatus.PENDING ? 'Scanning File' : row.docStatus ? properCase(row.docStatus) : ''}
                </TableCell>
                <TableCell align={'right'}>
                  <Button
                    variant="contained"
                    className={classes.buttonSmall}
                    disabled={row.docStatus !== EnumFileStatus.RECEIVED}
                    onClick={() => handleToggle(true)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </CustomTable>
      </LoadingOverlay>
      <Dialog open={open} onClose={() => handleToggle(false)} aria-labelledby="form-dialog-app">
        <DialogTitle id="form-dialog-app">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To delete this application, type in the word <b>permanently delete</b> then click Submit.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="confirm"
            label="type 'permanently delete' to confirm this action"
            type="text"
            fullWidth
            onChange={handleConfirmType}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleToggle(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAppDialogDelete} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};

export default FileListGrid;
